// @figma file=nq2oTuNpAeyLOoF0tXtO32 node=3724-120666 name="Badge" set="shadcn-studio-pro-v5" verified=2026-03-16

import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Base ─────────────────────────────────────────────────────────────────────

export const badgeBase =
  'inline-flex items-center font-medium whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 [&_svg]:pointer-events-none [&_svg]:shrink-0';

// ─── Variant config ───────────────────────────────────────────────────────────

export const badgeVariantConfig = {
  variants: {
    /**
     * Color intent — matches Figma `Style` dimension.
     * Maps to semantic tokens: bg-{variant}, text-{variant}-foreground, etc.
     */
    variant: {
      primary: '',
      secondary: '',
      info: '',
      success: '',
      warning: '',
      destructive: '',
    },
    /**
     * Visual appearance — matches Figma badge type dimension.
     * solid   → filled background
     * soft    → semi-transparent tinted background
     * outline → border only, transparent background
     * dot     → colored dot indicator + muted text, no background
     */
    appearance: {
      solid: '',
      soft: '',
      outline: 'border bg-transparent',
      dot: 'bg-transparent',
    },
    /**
     * Size — matches Figma `Size` dimension.
     * sm → h-5  (Figma sm = 20px)
     * md → h-[22px] (Figma md = 22px)
     * lg → h-[26px] (Figma lg = 26px)
     */
    size: {
      sm: 'h-5 px-1.5 gap-0.5 text-xs [&_svg]:size-3',
      md: 'h-[22px] px-2 gap-1 text-xs [&_svg]:size-3',
      lg: 'h-[26px] px-3 gap-1.5 text-xs [&_svg]:size-3',
    },
    /**
     * Shape — matches Figma `Shape` dimension.
     * round   → fully rounded pill (Figma: Shape=Round)
     * rounded → uses --radius token (Figma: Shape=Rounded)
     */
    shape: {
      round: 'rounded-full',
      rounded: 'rounded-[var(--radius)]',
    },
  },

  compoundVariants: [
    // ── Solid ──────────────────────────────────────────────────────────────────
    {
      variant: 'primary' as const,
      appearance: 'solid' as const,
      class: 'bg-primary text-primary-foreground hover:bg-primary/80',
    },
    {
      variant: 'secondary' as const,
      appearance: 'solid' as const,
      class: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    },
    {
      variant: 'info' as const,
      appearance: 'solid' as const,
      class: 'bg-info text-info-foreground hover:bg-info/80',
    },
    {
      variant: 'success' as const,
      appearance: 'solid' as const,
      class: 'bg-success text-success-foreground hover:bg-success/80',
    },
    {
      variant: 'warning' as const,
      appearance: 'solid' as const,
      class: 'bg-warning text-warning-foreground hover:bg-warning/80',
    },
    {
      variant: 'destructive' as const,
      appearance: 'solid' as const,
      class: 'bg-destructive text-destructive-foreground hover:bg-destructive/80',
    },

    // ── Soft ───────────────────────────────────────────────────────────────────
    {
      variant: 'primary' as const,
      appearance: 'soft' as const,
      class: 'bg-primary/10 text-primary hover:bg-primary/20',
    },
    {
      variant: 'secondary' as const,
      appearance: 'soft' as const,
      class: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
    },
    {
      variant: 'info' as const,
      appearance: 'soft' as const,
      class: 'bg-info/10 text-info hover:bg-info/20',
    },
    {
      variant: 'success' as const,
      appearance: 'soft' as const,
      class: 'bg-success/10 text-success hover:bg-success/20',
    },
    {
      variant: 'warning' as const,
      appearance: 'soft' as const,
      class: 'bg-warning/10 text-warning hover:bg-warning/20',
    },
    {
      variant: 'destructive' as const,
      appearance: 'soft' as const,
      class: 'bg-destructive/10 text-destructive hover:bg-destructive/20',
    },

    // ── Outline ────────────────────────────────────────────────────────────────
    {
      variant: 'primary' as const,
      appearance: 'outline' as const,
      class: 'border-primary text-primary hover:bg-primary/10',
    },
    {
      variant: 'secondary' as const,
      appearance: 'outline' as const,
      class: 'border-input text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
    },
    {
      variant: 'info' as const,
      appearance: 'outline' as const,
      class: 'border-info text-info hover:bg-info/10',
    },
    {
      variant: 'success' as const,
      appearance: 'outline' as const,
      class: 'border-success text-success hover:bg-success/10',
    },
    {
      variant: 'warning' as const,
      appearance: 'outline' as const,
      class: 'border-warning text-warning hover:bg-warning/10',
    },
    {
      variant: 'destructive' as const,
      appearance: 'outline' as const,
      class: 'border-destructive text-destructive hover:bg-destructive/10',
    },

    // ── Dot ────────────────────────────────────────────────────────────────────
    // Dot badge: text is always muted; the variant drives the dot color (see dotColorMap below)
    { variant: 'primary' as const, appearance: 'dot' as const, class: 'text-muted-foreground' },
    { variant: 'secondary' as const, appearance: 'dot' as const, class: 'text-muted-foreground' },
    { variant: 'info' as const, appearance: 'dot' as const, class: 'text-muted-foreground' },
    { variant: 'success' as const, appearance: 'dot' as const, class: 'text-muted-foreground' },
    { variant: 'warning' as const, appearance: 'dot' as const, class: 'text-muted-foreground' },
    { variant: 'destructive' as const, appearance: 'dot' as const, class: 'text-muted-foreground' },
  ],

  defaultVariants: {
    variant: 'primary' as const,
    appearance: 'solid' as const,
    size: 'md' as const,
    shape: 'round' as const,
  },
};

const badgeVariants = cva(badgeBase, badgeVariantConfig);

// ─── Dot color map ─────────────────────────────────────────────────────────────
// Maps variant → the background class used for the dot indicator
const dotColorMap = {
  primary: 'bg-primary',
  secondary: 'bg-secondary-foreground',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
} as const;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Left-side icon slot — accepts any ReactNode (Lucide icon, SVG, etc.) */
  icon?: React.ReactNode;
  /** Renders a dismiss × button; receives the click handler */
  onDismiss?: React.MouseEventHandler<HTMLButtonElement>;
}

// ─── Component ────────────────────────────────────────────────────────────────

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant = 'primary',
      appearance = 'solid',
      size = 'md',
      shape = 'round',
      icon,
      onDismiss,
      children,
      ...props
    },
    ref,
  ) => {
    const isDot = appearance === 'dot';

    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, appearance, size, shape }), className)}
        {...props}
      >
        {/* Dot indicator — only rendered when appearance='dot' */}
        {isDot && (
          <span
            aria-hidden
            className={cn('shrink-0 rounded-full size-2', dotColorMap[variant ?? 'primary'])}
          />
        )}

        {/* Left icon slot — not shown when appearance='dot' (dot acts as the icon) */}
        {!isDot && icon && <span className="shrink-0 [&_svg]:size-[1em]">{icon}</span>}

        {children}

        {/* Dismiss button */}
        {onDismiss && (
          <button
            type="button"
            aria-label="Dismiss"
            onClick={onDismiss}
            className="shrink-0 ml-0.5 rounded-full opacity-70 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-current"
          >
            <X />
          </button>
        )}
      </span>
    );
  },
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
